import React, { useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { EmploymentStatus, PensionContext, PensionParameters } from '../../../contexts/pension-robot/PensionContext';
import { Tooltip } from '../../general/Tooltip';
import { TooltipIndicator } from '../../general/TooltipIndicator';
import { NextButton } from './NavigationButtons';
import { YesNoFormGroup } from '../../general/form-inputs/YesNoFormGroup';
import { AgeInput } from '../../general/AgeInput';
import { CocktailWrapperScreen } from './CocktailWrapperScreen';
import { FormGroup } from '../../general/form-inputs/FormGroup';
import { SelectInput } from '../../general/form-inputs/SelectInput';
import { CurrencyFormGroup } from '../../general/form-inputs/CurrencyFormGroup';
import { isPensionCalculatedTogether } from '../../../utils/functions';
import { RobotEnvironmentContext } from '../../../contexts/general/RobotEnvironmentContext';
import { MIN_AGE_CLIENT } from '../../../config/constants';
import { useGetPensionCalcData } from '../../../api/pension-robot/useGetPensionCalcData';

export const PersonalInfoSection: React.FC<{}> = () => {
    const { t } = useTranslation('pension');

    return (
        <CocktailWrapperScreen page={1} title={t('pension-cocktail-fill-screen-personal-title')}>
            <PersonalInfoInputs />
        </CocktailWrapperScreen>
    );
};

export const PersonalInfoInputs: React.FC<{}> = () => {
    const { t } = useTranslation('pension');
    const {
        parameters: { personalInfo },
        actions,
    } = useContext(PensionContext);
    const pensionCalcData = useGetPensionCalcData().data;
    const { environment } = useContext(RobotEnvironmentContext);

    const MarketingTooltip = () => {
        const bodyElement = (
            <span>
                {t('pension-cocktail-fill-screen-personal-beginner-help-body-part-1-marketing')}
                <br />
                <br />
                {t('pension-cocktail-fill-screen-personal-beginner-help-body-part-2-general')}
            </span>
        );

        return (
            <Tooltip
                tooltipTitle={t('pension-cocktail-fill-screen-personal-beginner-help-title')}
                tooltipElement={bodyElement}
                className="ml-2"
            >
                <TooltipIndicator>?</TooltipIndicator>
            </Tooltip>
        );
    };

    const MybndTooltip = () => {
        const bodyElement = (
            <span>
                {t('pension-cocktail-fill-screen-personal-beginner-help-body-part-1-mybnd')}
                <br />
                <br />
                {t('pension-cocktail-fill-screen-personal-beginner-help-body-part-2-general')}
            </span>
        );

        return (
            <Tooltip
                tooltipTitle={t('pension-cocktail-fill-screen-personal-beginner-help-title')}
                tooltipElement={bodyElement}
                className="ml-2"
            >
                <TooltipIndicator>?</TooltipIndicator>
            </Tooltip>
        );
    };

    const getToolTip = () => {
        if (environment === 'Marketing') return <MarketingTooltip />;

        if (environment === 'MyBND') return <MybndTooltip />;
    };

    return (
        <>
            <AgeInput
                title={{
                    label: t('pension-cocktail-fill-screen-personal-question-age'),
                    tooltip: { text: t('pension-cocktail-fill-screen-personal-question-age-tooltip-text') },
                }}
                value={personalInfo?.self?.age}
                onChange={(value) => {
                    actions.setParameters({ personalInfo: { self: { age: value } } });
                }}
                minAge={MIN_AGE_CLIENT}
                maxAge={pensionCalcData?.legalRetirementAge ? pensionCalcData?.legalRetirementAge - 1 : undefined}
                className="mb-8"
            />
            <EmploymentStatusSelectInput
                title={{
                    label: t('pension-cocktail-fill-screen-personal-question-employment-status'),
                }}
                value={personalInfo?.self?.employmentStatus}
                onChange={(value) => {
                    actions.setParameters({
                        personalInfo: { self: { employmentStatus: value } },
                    });
                }}
                className="mb-8"
            />
            <CurrencyFormGroup
                title={{
                    label: t('pension-cocktail-fill-screen-personal-question-salary'),
                }}
                value={personalInfo?.self?.salary}
                onChange={(cents) => {
                    actions.setParameters({ personalInfo: { self: { salary: cents } } });
                }}
                className="mb-8"
            />
            <MaritalStatusSelectInput
                title={{
                    label: t('pension-cocktail-fill-screen-personal-question-marital-status'),
                }}
                value={personalInfo?.maritalStatus}
                onChange={(value) => {
                    actions.setParameters({
                        personalInfo: {
                            maritalStatus: value as PensionParameters['personalInfo']['maritalStatus'],
                        },
                    });
                }}
                className="mb-8"
            />
            {personalInfo?.maritalStatus === 'married' && (
                <>
                    <YesNoFormGroup
                        className="mb-8"
                        title={{
                            label: t('pension-cocktail-fill-screen-personal-question-calculate-together'),
                            tooltip: {
                                text: t(
                                    'pension-cocktail-fill-screen-personal-question-calculate-together-tooltip-text',
                                ),
                            },
                        }}
                        value={
                            personalInfo?.isCalculatedTogether === undefined
                                ? null
                                : personalInfo?.isCalculatedTogether
                                ? 'yes'
                                : 'no'
                        }
                        onChange={(value) => {
                            if (value === 'yes') {
                                actions.setParameters({ personalInfo: { isCalculatedTogether: true } });
                            }
                            if (value === 'no') {
                                actions.setParameters({ personalInfo: { isCalculatedTogether: false } });
                            }
                        }}
                    />
                    {!!personalInfo.isCalculatedTogether && <PartnerInputs />}
                </>
            )}

            <div className="flex flex-row flex-wrap justify-between items-center gap-4 my-8">
                <NextButton nextPage="expenses" isDisabled={!isValid(personalInfo)} />
                <div className="flex items-center">
                    {t('pension-cocktail-fill-screen-personal-beginner-help')}
                    {getToolTip()}
                </div>
            </div>
        </>
    );
};

function isValid(parameters: PensionParameters['personalInfo']): boolean {
    const validInfo =
        !!parameters.self?.age &&
        !!parameters.self?.employmentStatus &&
        parameters.self?.salary !== undefined &&
        !!parameters.maritalStatus;

    const withPartner = isPensionCalculatedTogether(parameters);
    if (withPartner) {
        return (
            validInfo &&
            !!parameters.partner?.age &&
            !!parameters.partner?.employmentStatus &&
            parameters.partner?.salary !== undefined
        );
    }

    return validInfo;
}

const EmploymentStatusSelectInput: React.FC<
    {
        value?: EmploymentStatus;
        onChange: (value: EmploymentStatus) => void;
    } & React.ComponentProps<typeof FormGroup>
> = ({ value, onChange, ...rest }) => {
    const { t } = useTranslation('pension');
    return (
        <FormGroup {...rest}>
            <SelectInput
                onChange={(_value) => onChange(_value as EmploymentStatus)}
                selected={value || null}
                values={[
                    {
                        label: t('pension-cocktail-fill-screen-personal-question-employment-employee'),
                        value: 'employee',
                    },
                    {
                        label: t('pension-cocktail-fill-screen-personal-question-employment-zzp'),
                        value: 'self-employed',
                    },
                    {
                        label: t('pension-cocktail-fill-screen-personal-question-employment-unemployed'),
                        value: 'unemployed',
                    },
                ]}
            />
        </FormGroup>
    );
};

const MaritalStatusSelectInput: React.FC<
    {
        value?: PensionParameters['personalInfo']['maritalStatus'];
        onChange: (value: string) => void;
    } & React.ComponentProps<typeof FormGroup>
> = ({ value, onChange, ...rest }) => {
    const { t } = useTranslation('pension');
    return (
        <FormGroup {...rest}>
            <SelectInput
                values={[
                    {
                        label: t('pension-cocktail-fill-screen-personal-question-marital-status-single'),
                        value: 'single',
                    },
                    { label: t('pension-cocktail-fill-screen-personal-question-marital-status-wed'), value: 'married' },
                ]}
                selected={value || null}
                onChange={onChange}
            />
        </FormGroup>
    );
};

const PartnerInputs: React.FC<{}> = () => {
    const { t } = useTranslation('pension');
    const {
        parameters: { personalInfo },
        actions,
    } = useContext(PensionContext);
    const pensionCalcData = useGetPensionCalcData().data;
    return (
        <>
            <AgeInput
                title={{
                    label: t('pension-cocktail-fill-screen-personal-question-partner-age'),
                    tooltip: { text: t('pension-cocktail-fill-screen-personal-question-partner-age-tooltip-text') },
                }}
                value={personalInfo?.partner?.age ? personalInfo?.partner?.age : undefined}
                onChange={(value) => {
                    actions.setParameters({ personalInfo: { partner: { age: value } } });
                }}
                minAge={MIN_AGE_CLIENT}
                maxAge={pensionCalcData?.legalRetirementAge ? pensionCalcData?.legalRetirementAge - 1 : undefined}
                className="mb-8"
            />
            <EmploymentStatusSelectInput
                title={{
                    label: t('pension-cocktail-fill-screen-personal-question-partner-employment-status'),
                }}
                value={personalInfo?.partner?.employmentStatus}
                onChange={(value) => {
                    actions.setParameters({
                        personalInfo: { partner: { employmentStatus: value } },
                    });
                }}
                className="mb-8"
            />
            <CurrencyFormGroup
                title={{
                    label: t('pension-cocktail-fill-screen-personal-question-partner-salary'),
                }}
                value={personalInfo?.partner?.salary}
                onChange={(cents) => {
                    actions.setParameters({ personalInfo: { partner: { salary: cents } } });
                }}
                className="mb-8"
            />
        </>
    );
};
